import { t, Trans } from "@lingui/macro";
import { IApiSmpGptAgent } from "@smp/api";
import {
  IUiSelectProps,
  UiBox,
  UiButton,
  UiCheckboxV2,
  UiChip,
  UiFormControlLabel,
  UiHelper,
  UiSelect,
  UiTypography,
} from "@smp/ui";
import { dataArrayAddOrRemove, dataArrayToHashMap } from "@smp/utils";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { FC, useMemo } from "react";

import { UiGptLanguageSelect } from "@/components/ui/gpt_language_select";
import {
  GPT_POST_EXTRA_FIELDS,
  GPT_POST_FRAMEWORKS,
  GPT_TEXT_LENGHT_PRESETS,
  GPT_TEXT_STYLES,
} from "@/fixtures";
import { dialogAdvisorConfig } from "@/screens/app/components/dialogs/dialog_advisor";
import { FormNumberTextField } from "@/screens/app/components/dialogs/dialog_advisor/components/form_number_text_field";

import localClasses from "./post_params.module.css";

export interface IPostParamsProps {
  disabled?: boolean;
  classes?: Partial<{
    group: string;
    row: string;
    row_col: string;
    row__col: string;
    radio: string;
  }>;
}

export const PostParams: FC<IPostParamsProps> = ({ disabled, classes }) => {
  const { values, setFieldValue } =
    useFormikContext<IApiSmpGptAgent["params"]>();

  const styleOptions = useMemo<IUiSelectProps["options"]>(
    () =>
      GPT_TEXT_STYLES.map((style) => ({
        label: style.getDisplayName(),
        value: style.id,
      })),
    [],
  );

  const styleOptionsNormalized = useMemo(
    () => dataArrayToHashMap(styleOptions, "value"),
    [styleOptions],
  );

  return (
    <div className={classes?.group}>
      <div className={clsx(classes?.row, classes?.row_col)}>
        <div className={classes?.row__col}>
          <UiTypography variant="h5">
            <Trans>Number of posts</Trans>
          </UiTypography>

          <FormNumberTextField
            value={values?.post?.num || dialogAdvisorConfig.min_posts_num}
            inputProps={{
              min: dialogAdvisorConfig.min_posts_num,
              max: dialogAdvisorConfig.max_posts_num,
            }}
            onChange={(e) => setFieldValue("post.num", e.target.value)}
            disabled={disabled}
          />
        </div>

        <div className={classes?.row__col}>
          <UiTypography variant="h5">
            <Trans>Language</Trans>
          </UiTypography>

          <UiGptLanguageSelect
            value={values?.post?.language || ""}
            onChange={(lang) => setFieldValue("post.language", lang)}
            emojiSize="large"
            isDisabled={disabled}
          />
        </div>
      </div>

      <div className={classes?.row}>
        <UiTypography variant="h5">
          <Trans>Posts length</Trans>
        </UiTypography>

        <div className={classes?.radio}>
          {GPT_TEXT_LENGHT_PRESETS.map((preset) => {
            const isActive = values?.post?.text_length === preset.id;

            return (
              <UiButton
                key={preset.id}
                className={clsx(
                  localClasses.radio,
                  isActive && localClasses.radio_active,
                )}
                size="small"
                onClick={() => setFieldValue("post.text_length", preset.id)}
                disabled={disabled}
              >
                {preset.getDisplayName()}
              </UiButton>
            );
          })}
        </div>
      </div>

      <div className={classes?.row}>
        <UiTypography variant="h5">
          <Trans>Text style</Trans>
        </UiTypography>

        <UiSelect
          options={styleOptions}
          fullWidth
          value={values?.post?.text_style?.at(0) || ""}
          renderValue={(value) => (
            <UiBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                margin: "-5px 0 -6px",
              }}
            >
              <UiChip label={styleOptionsNormalized[value as string].label} />
            </UiBox>
          )}
          onChange={(e) => {
            setFieldValue("post.text_style", [e.target.value]);
          }}
          disabled={disabled}
        />
      </div>

      <div className={classes?.row}>
        <div className={localClasses?.title}>
          <UiTypography variant="h5">
            <Trans>Add to post</Trans>
          </UiTypography>
          <UiHelper
            title={t`Ask the AI SMM assistant to add
             title or call to action to the post`}
            arrow={false}
            size="big"
          />
        </div>

        {GPT_POST_EXTRA_FIELDS.map((field) => {
          const checked = !!values?.post?.extra_fields?.includes(field.id);

          return (
            <UiFormControlLabel
              key={field.id}
              control={
                <UiCheckboxV2
                  checked={checked}
                  onChange={() => {
                    setFieldValue(
                      "post.extra_fields",
                      dataArrayAddOrRemove(
                        values?.post?.extra_fields || [],
                        field.id,
                      ),
                    );
                  }}
                  disabled={disabled}
                />
              }
              label={field.getDisplayName()}
            />
          );
        })}
      </div>

      <div className={classes?.row}>
        <UiTypography variant="h5">
          <Trans>Framework</Trans>
        </UiTypography>

        {GPT_POST_FRAMEWORKS.map((framework) => {
          const checked = !!values?.post?.frameworks?.includes(framework.id);

          return (
            <UiFormControlLabel
              key={framework.id}
              control={
                <UiCheckboxV2
                  checked={checked}
                  onChange={() => {
                    setFieldValue(
                      "post.frameworks",
                      dataArrayAddOrRemove(
                        values?.post?.frameworks || [],
                        framework.id,
                      ),
                    );
                  }}
                  disabled={disabled}
                />
              }
              label={framework.getDisplayName()}
            />
          );
        })}
      </div>
    </div>
  );
};
